// standards-ignore: co-located-test — presentational-only component with no testable logic; renders a Stencil PopupDateRangePicker with static props
import { useId } from 'react';
import { ExpanderContent } from '@amzn/stencil-react-components/expander';
import { PopupDateRangePicker } from '@amzn/stencil-react-components/popup-date-range-picker';
// Deep import: the public 'filtering' entry doesn't re-export the hooks,
// but useFilterContext is the only way to read the panel's PENDING radio
// selection. Inside the "All filters" flyout, FilterPanelList never calls
// onFilterChange (selections only commit on Apply), so props can't tell us
// the user just clicked "Custom" — the FilterContext can.
import { useFilterContext } from '@amzn/stencil-react-components/dist/submodules/filtering/hooks';

import { useMessages } from '../i18n/useMessages';
import { CUSTOM_DATE_RANGE_TYPE } from '../domain';

const LIST_BUNDLE = 'shared.ui.translations.dateRangeFilter';
type Key = 'dateRangeStartLabel' | 'dateRangeEndLabel';

export interface CustomDateRangeFilterProps {
  startDate: string | undefined;
  endDate: string | undefined;
  onRangeChange: (start: string | undefined, end: string | undefined) => void;
}

/**
 * Custom date range picker rendered inside a `dateRange` FilterController.
 *
 * Visibility comes from the FilterController's FilterContext selection (the
 * pending radio choice), so the picker appears the moment "Custom" is
 * clicked — even inside the flyout, where onFilterChange only fires on Apply.
 *
 * Must be a component (not inline `{cond && <picker/>}`) because FilterBar
 * introspects each FilterController child's element type — a bare `false`
 * in the children array crashes its renderFilters.
 *
 * All dates (past and future) are selectable so users can plan for
 * upcoming audits/tasks.
 */
export function CustomDateRangeFilter({
  startDate,
  endDate,
  onRangeChange,
}: CustomDateRangeFilterProps): JSX.Element {
  const { t } = useMessages<Key>(LIST_BUNDLE);
  const { selection } = useFilterContext();
  // React 18's useId gives a stable, SSR-safe, guaranteed-unique DOM id
  // per mount. Multiple filter bars on the same page (or a StrictMode
  // double-mount) can't collide, and callers don't need to invent an id
  // string — that concern is entirely encapsulated here.
  const id = useId();

  // Stays mounted; ExpanderContent animates its own reveal. Conditionally
  // mounting the picker instead causes a show-clip-show flicker: the parent
  // filter expander's maxHeight animation lags the sudden content growth
  // and clips the new content while it catches up.
  return (
    <ExpanderContent isExpanded={selection === CUSTOM_DATE_RANGE_TYPE}>
      <PopupDateRangePicker
        id={id}
        startDateLabelText={t('dateRangeStartLabel')}
        endDateLabelText={t('dateRangeEndLabel')}
        // Under exactOptionalPropertyTypes, only pass the value when set;
        // the picker treats an omitted prop as "no selection."
        {...(startDate !== undefined ? { startValue: startDate } : {})}
        {...(endDate !== undefined ? { endValue: endDate } : {})}
        isDateDisabled={() => false}
        onChange={onRangeChange}
      />
    </ExpanderContent>
  );
}
